/**
 * Tiny IndexedDB wrapper — just enough for the offline write queue.
 *
 * Avoids the `idb` npm package so we don't bloat the bundle or add
 * a dependency just for a v1 feature. ~80 lines of vanilla DOM API.
 *
 * Schema:
 *   Database: "qs-offline" v1
 *   Object store: "pending_writes"  (keyPath: "id")
 *   Index:        "enqueued_at"     (for FIFO drain order)
 *
 * Each record shape:
 * {
 *   id:                string (uuid — also the Idempotency-Key)
 *   method:            "POST" | "PUT" | "PATCH" | "DELETE"
 *   url:               string  (full URL, includes /api prefix)
 *   body:              any     (will be JSON.stringify'd at send time)
 *   headers:           Record<string,string>
 *   enqueued_at:       ISO datetime string
 *   attempts:          number
 *   status:            "pending" | "syncing" | "synced" | "failed"
 *   last_error:        string | null
 *   last_attempt_at:   ISO datetime string | null
 *   label:             string  (human-readable — "Customer: Jane Smith")
 * }
 */

const DB_NAME  = "qs-offline";
const DB_VER   = 1;
const STORE    = "pending_writes";

let _dbPromise = null;

const openDb = () => {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB not supported in this browser."));
      return;
    }
    const req = window.indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("enqueued_at", "enqueued_at");
        store.createIndex("status",      "status");
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
  return _dbPromise;
};

const tx = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let result;
    Promise.resolve(fn(store))
      .then((r) => { result = r; })
      .catch(reject);
    t.oncomplete = () => resolve(result);
    t.onerror    = (e) => reject(e.target.error);
    t.onabort    = (e) => reject(e.target.error || new Error("tx aborted"));
  });
};

export const idbPut = (record) =>
  tx("readwrite", (store) => new Promise((res, rej) => {
    const r = store.put(record);
    r.onsuccess = () => res(record);
    r.onerror = (e) => rej(e.target.error);
  }));

export const idbGet = (id) =>
  tx("readonly", (store) => new Promise((res, rej) => {
    const r = store.get(id);
    r.onsuccess = () => res(r.result || null);
    r.onerror = (e) => rej(e.target.error);
  }));

export const idbDelete = (id) =>
  tx("readwrite", (store) => new Promise((res, rej) => {
    const r = store.delete(id);
    r.onsuccess = () => res();
    r.onerror = (e) => rej(e.target.error);
  }));

export const idbAll = () =>
  tx("readonly", (store) => new Promise((res, rej) => {
    // Walk the enqueued_at index so callers always get FIFO order.
    const idx = store.index("enqueued_at");
    const out = [];
    const cursor = idx.openCursor();
    cursor.onsuccess = (e) => {
      const c = e.target.result;
      if (c) { out.push(c.value); c.continue(); }
      else { res(out); }
    };
    cursor.onerror = (e) => rej(e.target.error);
  }));

export const idbClear = () =>
  tx("readwrite", (store) => new Promise((res, rej) => {
    const r = store.clear();
    r.onsuccess = () => res();
    r.onerror = (e) => rej(e.target.error);
  }));
